import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import LibrarySection from './components/LibrarySection';
import MusicLibrarySection from './components/MusicLibrarySection';
import ComunidadSection from './components/ComunidadSection';
import MasAllaSection from './components/MasAllaSection';
import MercedesVideoSection from './components/MercedesVideoSection';
import TestSection from './components/TestSection';
import EmergencyGuideBanner from './components/EmergencyGuideBanner';
import { programs, freeTutorials, homeHeroImage } from '@/mocks/neurobaby';

export default function Home() {
  const { t } = useTranslation();
  const location = useLocation();
  const [hoveredProgram, setHoveredProgram] = useState<number | null>(null);

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-cream overflow-x-hidden">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[600px] md:min-h-[720px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={homeHeroImage}
            alt={t('home.hero.badge')}
            className="w-full h-full object-cover object-[55%_30%]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/20 to-transparent" />
        </div>
        <div className="relative z-10 min-h-[600px] md:min-h-[720px] flex flex-col justify-between">
          <div className="pt-20 md:pt-24" />
          <div className="w-full px-6 md:px-10 lg:px-16 flex-1 flex flex-col justify-center">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 w-full">
              <div className="lg:col-span-5 flex flex-col justify-center text-left">
                <span className="inline-block font-sans text-xs md:text-sm font-semibold text-white uppercase tracking-[0.2em] mb-5 bg-white/15 backdrop-blur-sm px-4 py-2 rounded-full w-fit">
                  {t('home.hero.badge')}
                </span>
                <h1 className="font-serif text-[clamp(1.75rem,3.5vw,3rem)] font-bold text-white leading-[1.1] md:whitespace-nowrap mb-1">
                  {t('home.hero.quote')}
                </h1>
                <p className="font-serif text-xs md:text-sm text-white mb-5">
                  {t('home.hero.quote_author')}
                </p>
                <p className="font-sans text-sm md:text-base text-white leading-relaxed max-w-md mb-6">
                  {t('home.hero.text1')}
                </p>
                <p className="font-sans text-xs md:text-sm text-white leading-relaxed max-w-md">
                  {t('home.hero.text2')}
                </p>
              </div>
              <div className="lg:col-span-7" />
            </div>
          </div>
          <div className="w-full px-6 md:px-10 lg:px-16 pb-10 md:pb-14">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 w-full">
              <div className="lg:col-span-5">
                <div className="flex flex-col sm:flex-row items-start gap-4 mb-5">
                  <Link
                    to="/quiero-cambiar"
                    className="inline-flex items-center justify-center bg-white text-[#3D2B1F] px-8 py-4 rounded-full font-sans text-sm font-semibold hover:bg-cream transition-colors duration-300 whitespace-nowrap"
                  >
                    {t('common.quiero_cambiar')}
                  </Link>
                  <Link
                    to="/conoce-el-metodo"
                    className="inline-flex items-center justify-center bg-terracotta text-white px-8 py-4 rounded-full font-sans text-sm font-semibold hover:bg-brown transition-colors duration-300 whitespace-nowrap"
                  >
                    {t('home.hero.conocer_metodo')}
                    <i className="ri-arrow-right-line ml-2" />
                  </Link>
                </div>
                <div className="flex flex-wrap items-start gap-x-6 gap-y-2 text-white">
                  <div className="flex items-center gap-2">
                    <i className="ri-check-line text-terracotta" />
                    <span className="font-sans text-sm">{t('home.hero.acceso_vida')}</span>
                  </div>
                </div>
              </div>
              <div className="lg:col-span-7" />
            </div>
          </div>
        </div>
      </section>

      {/* Mercedes Video Section — Bienvenida */}
      <MercedesVideoSection />

      {/* Programs Section */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-cream">
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-4">
              {t('home.programs.subtitle')}
            </span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-[#8B6F5C] leading-tight mb-4">
              {t('home.programs.title')}
            </h2>
            <p className="font-sans text-base text-[#3D2B1F] max-w-2xl mx-auto">
              {t('home.programs.desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {programs.map((program) => (
              <div
                key={program.id}
                className="bg-white rounded-2xl overflow-hidden border border-sand/40 transition-all duration-300 hover:shadow-lg hover:border-terracotta/30 flex flex-col"
                onMouseEnter={() => setHoveredProgram(program.id)}
                onMouseLeave={() => setHoveredProgram(null)}
              >
                <div className="h-44 overflow-hidden relative">
                  <img
                    src={program.image}
                    alt={t(`programs.items.${program.id}.title`, program.title)}
                    className="w-full h-full object-cover transition-transform duration-500"
                    style={{
                      transform: hoveredProgram === program.id ? 'scale(1.05)' : 'scale(1)',
                    }}
                  />
                  <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                    <span className="font-sans text-xs font-semibold text-[#8B6F5C]">
                      {t(`programs.items.${program.id}.level`, program.level)}
                    </span>
                  </div>
                  {'badge' in program && (program as any).badge && (
                    <div className="absolute top-3 right-3 bg-terracotta text-white px-3 py-1 rounded-full">
                      <span className="font-sans text-xs font-semibold">{(program as any).badge}</span>
                    </div>
                  )}
                </div>
                <div className="p-5 flex flex-col flex-grow">
                  <span className="font-sans text-xs text-[#5C4A3A] mb-1">
                    {t(`programs.items.${program.id}.subtitle`, program.subtitle)}
                  </span>
                  <h3 className="font-serif text-xl font-bold text-[#8B6F5C] mb-2 leading-tight">
                    {t(`programs.items.${program.id}.title`, program.title)}
                  </h3>
                  <p className="font-sans text-sm text-[#3D2B1F] leading-relaxed mb-4 flex-grow">
                    {t(`programs.items.${program.id}.description`, program.description)}
                  </p>
                  <div className="flex items-center gap-3 mb-4 text-sm text-[#5C4A3A]">
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line text-xs" />
                      {program.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-book-open-line text-xs" />
                      {program.lessons} {t('common.guias')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-sand/30">
                    <div>
                      <span className="font-sans text-2xl font-bold text-[#8B6F5C]">${program.price}</span>
                      <span className="font-sans text-xs text-[#5C4A3A] ml-1">{t('common.usd')}</span>
                    </div>
                    <Link
                      to={program.id === 1 ? "/fundamentos-sueno" : program.id === 2 ? "/yoga-sueno-profundo" : "/programa-completo-neurobabys"}
                      className="inline-flex items-center bg-terracotta text-white px-5 py-2.5 rounded-full font-sans text-sm font-semibold hover:bg-brown transition-colors duration-300 whitespace-nowrap"
                    >
                      {t('common.conocer_mas')}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Test del Sueño Section */}
      <TestSection />

      {/* Free Tutorials Section */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-sand">
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-4">
              {t('home.free.subtitle')}
            </span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-[#8B6F5C] leading-tight mb-4">
              {t('home.free.title')}
            </h2>
            <p className="font-sans text-base text-[#3D2B1F] max-w-2xl mx-auto">
              {t('home.free.desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {freeTutorials.map((tutorial, index) => (
              <div
                key={tutorial.id}
                className="bg-white rounded-2xl overflow-hidden group cursor-pointer"
              >
                <div className="h-48 overflow-hidden relative">
                  <img
                    src={tutorial.image}
                    alt={t(`free_tutorials.${tutorial.id}.title`, tutorial.title)}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-1.5">
                    <i className="ri-play-circle-line text-white text-xs" />
                    <span className="font-sans text-xs text-white font-medium">{tutorial.duration}</span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-sans text-base font-bold text-[#8B6F5C] mb-3 leading-snug">
                    {t(`free_tutorials.${tutorial.id}.title`, tutorial.title)}
                  </h3>
                  {index === 0 ? (
                    <Link
                      to="/guia-senales-sueno"
                      className="inline-flex items-center font-sans text-sm font-semibold text-terracotta hover:text-brown transition-colors duration-300 group/btn"
                    >
                      {t('common.ver_gratis')}
                      <i className="ri-arrow-right-line ml-1.5 transition-transform duration-300 group-hover/btn:translate-x-1" />
                    </Link>
                  ) : index === 1 ? (
                    <Link
                      to="/rutina-relajacion"
                      className="inline-flex items-center font-sans text-sm font-semibold text-terracotta hover:text-brown transition-colors duration-300 group/btn"
                    >
                      {t('common.ver_gratis')}
                      <i className="ri-arrow-right-line ml-1.5 transition-transform duration-300 group-hover/btn:translate-x-1" />
                    </Link>
                  ) : index === 2 ? (
                    <Link
                      to="/yoga-dormir-secuencia-nocturna"
                      className="inline-flex items-center font-sans text-sm font-semibold text-terracotta hover:text-brown transition-colors duration-300 group/btn"
                    >
                      {t('common.ver_gratis')}
                      <i className="ri-arrow-right-line ml-1.5 transition-transform duration-300 group-hover/btn:translate-x-1" />
                    </Link>
                  ) : index === 3 ? (
                    <Link
                      to="/ambiente-ideal-sueno"
                      className="inline-flex items-center font-sans text-sm font-semibold text-terracotta hover:text-brown transition-colors duration-300 group/btn"
                    >
                      {t('common.ver_gratis')}
                      <i className="ri-arrow-right-line ml-1.5 transition-transform duration-300 group-hover/btn:translate-x-1" />
                    </Link>
                  ) : index === 4 ? (
                    <Link
                      to="/regresiones-sueno"
                      className="inline-flex items-center font-sans text-sm font-semibold text-terracotta hover:text-brown transition-colors duration-300 group/btn"
                    >
                      {t('common.ver_gratis')}
                      <i className="ri-arrow-right-line ml-1.5 transition-transform duration-300 group-hover/btn:translate-x-1" />
                    </Link>
                  ) : index === 5 ? (
                    <Link
                      to="/meditacion-mamas-cansadas"
                      className="inline-flex items-center font-sans text-sm font-semibold text-terracotta hover:text-brown transition-colors duration-300 group/btn"
                    >
                      {t('common.ver_gratis')}
                      <i className="ri-arrow-right-line ml-1.5 transition-transform duration-300 group-hover/btn:translate-x-1" />
                    </Link>
                  ) : (
                    <span className="inline-flex items-center font-sans text-sm font-semibold text-[#5C4A3A]">
                      {t('common.proximamente')}
                      <i className="ri-time-line ml-1.5" />
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 md:mt-10">
            <EmergencyGuideBanner />
          </div>

          <div className="text-center mt-10">
            <Link
              to="/quiero-cambiar"
              className="inline-flex items-center gap-2 font-sans text-sm font-semibold text-[#8B6F5C] hover:text-terracotta transition-colors duration-300"
            >
              {t('home.free.ver_todo')}
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* Library Section */}
      <LibrarySection />

      {/* Biblioteca Musical NeuroBaby */}
      <MusicLibrarySection />

      {/* M&aacute;s all&aacute; de la maternidad &mdash; Libros */}
      <MasAllaSection />

      {/* Comunidad NeuroBaby &mdash; Timote Natural */}
      <ComunidadSection />

      {/* Final CTA Section */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-cream">
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-[#8B6F5C] leading-tight mb-4">
              {t('home.cta.title')}
            </h2>
            <p className="font-sans text-base text-[#3D2B1F] max-w-xl mx-auto mb-8">
              {t('home.cta.desc')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12 md:mb-16">
              <Link
                to="/quiero-cambiar"
                className="inline-flex items-center justify-center bg-white border-2 border-[#8B6F5C] text-[#8B6F5C] px-8 py-4 rounded-full font-sans text-sm font-semibold hover:bg-[#8B6F5C] hover:text-white transition-colors duration-300 whitespace-nowrap w-full sm:w-auto"
              >
                {t('common.empezar_gratis')}
              </Link>
              <Link
                to="/conoce-el-metodo"
                className="inline-flex items-center justify-center bg-terracotta text-white px-8 py-4 rounded-full font-sans text-sm font-semibold hover:bg-brown transition-colors duration-300 whitespace-nowrap w-full sm:w-auto"
              >
                {t('home.hero.conocer_metodo')}
                <i className="ri-arrow-right-line ml-2" />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {programs.map((program) => (
              <div
                key={program.id}
                className="group bg-white rounded-2xl overflow-hidden border border-sand/40 transition-all duration-300 hover:shadow-lg hover:border-terracotta/30 flex flex-col"
              >
                <div className="h-44 overflow-hidden relative">
                  <img
                    src={program.image}
                    alt={t(`programs.items.${program.id}.title`, program.title)}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                    <span className="font-sans text-xs font-semibold text-[#8B6F5C]">
                      {t(`programs.items.${program.id}.level`, program.level)}
                    </span>
                  </div>
                  {'badge' in program && (program as any).badge && (
                    <div className="absolute top-3 right-3 bg-terracotta text-white px-3 py-1 rounded-full">
                      <span className="font-sans text-xs font-semibold">{(program as any).badge}</span>
                    </div>
                  )}
                </div>
                <div className="p-5 flex flex-col flex-grow">
                  <span className="font-sans text-xs text-[#5C4A3A] mb-1">
                    {t(`programs.items.${program.id}.subtitle`, program.subtitle)}
                  </span>
                  <h3 className="font-serif text-xl font-bold text-[#8B6F5C] mb-2 leading-tight">
                    {t(`programs.items.${program.id}.title`, program.title)}
                  </h3>
                  <p className="font-sans text-sm text-[#3D2B1F] leading-relaxed mb-4 flex-grow">
                    {t(`programs.items.${program.id}.description`, program.description)}
                  </p>
                  <div className="flex items-center gap-3 mb-4 text-sm text-[#5C4A3A]">
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line text-xs" />
                      {program.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-book-open-line text-xs" />
                      {program.lessons} {t('common.guias')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-sand/30">
                    <div>
                      <span className="font-sans text-2xl font-bold text-[#8B6F5C]">${program.price}</span>
                      <span className="font-sans text-xs text-[#5C4A3A] ml-1">{t('common.usd')}</span>
                    </div>
                    <Link
                      to={program.id === 1 ? "/fundamentos-sueno" : program.id === 2 ? "/yoga-sueno-profundo" : "/programa-completo-neurobabys"}
                      className="inline-flex items-center bg-terracotta text-white px-5 py-2.5 rounded-full font-sans text-sm font-semibold hover:bg-brown transition-colors duration-300 whitespace-nowrap"
                    >
                      {t('common.conocer_mas')}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}